import { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import ImmersiveRecorder from '@/components/immersive/ImmersiveRecorder';
import { useDeepgramTranscriber } from '@/hooks/useDeepgramTranscriber';
import { useToast } from '@/components/ui/toast';
import PremiumModal from '@/components/PremiumModal';
import { useSubscription } from '@/hooks/useSubscription';
import {
  ArrowLeft, Mic, Send, Loader2, CalendarIcon,
  BookOpen,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { format, isToday } from 'date-fns';
import { ja } from 'date-fns/locale';
import { Clock } from 'lucide-react';

type Phase = 'recording' | 'editing' | 'saving';

export default function RecordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  const { isPremium, remainingSeconds } = useSubscription();
  const [phase, setPhase] = useState<Phase>(() =>
    searchParams.get('mode') === 'text' ? 'editing' : 'recording'
  );
  const [textInput, setTextInput] = useState('');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedTime, setSelectedTime] = useState('12:00');
  const [showCalendar, setShowCalendar] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  // 録音時間の蓄積（追加録音対応）
  const [recordedDuration, setRecordedDuration] = useState(0);

  // 辞書ルール追加
  const [selectedText, setSelectedText] = useState('');
  const [showDictDialog, setShowDictDialog] = useState(false);
  const [dictFind, setDictFind] = useState('');
  const [dictReplace, setDictReplace] = useState('');
  const [dictAdding, setDictAdding] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Saving state with progress
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveProgress, setSaveProgress] = useState(0);
  const [saveStatus, setSaveStatus] = useState('');

  // ─── Deepgram hook ───
  // Free: 1回の最大 = min(600秒, 月間残り秒数 - 既に録音した秒数)、Premium: 無制限(0)
  // 注意: useDeepgramTranscriber では 0 = 無制限なので、Free ユーザーは最低1秒を渡す
  const availableSeconds = remainingSeconds - recordedDuration;
  const maxDuration = isPremium ? 0 : Math.min(600, Math.max(1, availableSeconds));
  const deepgram = useDeepgramTranscriber({ maxDuration });
  const consumedTranscriptRef = useRef(0);
  const [pendingEditTransition, setPendingEditTransition] = useState(false);

  // 残り時間0秒以下のフリーユーザーが録音画面に入った場合、PremiumModal表示
  useEffect(() => {
    if (!isPremium && availableSeconds <= 0 && phase === 'recording' && searchParams.get('mode') !== 'text') {
      setShowPremiumModal(true);
      setPhase('editing');
    }
  }, [isPremium, availableSeconds, phase, searchParams]);

  // Draft auto-save
  const DRAFT_KEY = 'voicejournal_draft';
  useEffect(() => {
    const saved = localStorage.getItem(DRAFT_KEY);
    if (saved) {
      try {
        const draft = JSON.parse(saved);
        if (draft.text) {
          setTextInput(draft.text);
          setPhase('editing');
        }
      } catch { /* ignore */ }
    }
  }, []);

  useEffect(() => {
    if (textInput.trim()) {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ text: textInput, ts: Date.now() }));
    }
  }, [textInput]);

  const clearDraft = () => localStorage.removeItem(DRAFT_KEY);

  // テキスト選択検知
  const handleTextSelect = useCallback(() => {
    const sel = window.getSelection()?.toString().trim() || '';
    setSelectedText(sel);
  }, []);

  const openDictDialog = useCallback(() => {
    setDictFind(selectedText);
    setDictReplace('');
    setShowDictDialog(true);
  }, [selectedText]);

  const handleDictAdd = async () => {
    const find = dictFind.trim();
    const replace = dictReplace.trim();
    if (!find || !replace || find === replace) return;
    setDictAdding(true);
    try {
      await api.addVocabularyRule(find, replace);
      toast({ title: `「${find}→${replace}」を辞書に追加しました` });
      setShowDictDialog(false);
      // テキスト内の既存の誤変換も即座に置換
      setTextInput(prev => prev.split(find).join(replace));
    } catch (err) {
      toast({ title: '辞書への追加に失敗しました', variant: 'destructive' });
    } finally {
      setDictAdding(false);
    }
  };

  // ─── Discard handler ───
  const handleDiscard = () => {
    // 録音アクティブなら停止してから破棄
    if (deepgram.connectionStatus === 'recording' || deepgram.connectionStatus === 'paused') {
      deepgram.stopRecording();
    }
    clearDraft();
    setTextInput('');
    navigate('/');
  };

  const handleBackPress = () => {
    if (textInput.trim()) {
      setShowDiscardDialog(true);
    } else {
      clearDraft();
      navigate('/');
    }
  };

  /** 録音画面のキャンセル（赤Xボタン）— 常に確認ダイアログ表示 */
  const handleCancel = () => {
    setShowCancelDialog(true);
  };

  // ─── Keyboard shortcuts ───
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showCalendar) { setShowCalendar(false); return; }
        if (showDiscardDialog) { setShowDiscardDialog(false); return; }
        if (phase === 'recording') {
          if (deepgram.connectionStatus === 'recording' || deepgram.connectionStatus === 'paused') {
            deepgram.stopRecording();
            setPendingEditTransition(true);
          } else {
            handleBackPress();
          }
          return;
        }
        handleBackPress();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        if (phase === 'editing' && textInput.trim() && !isSubmitting) {
          handleSave();
        }
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (phase === 'editing' && textInput.trim() && !isSubmitting) {
          handleSave();
        }
        return;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [phase, textInput, isSubmitting, showCalendar, showDiscardDialog, deepgram.connectionStatus]);

  // ─── Sync hook transcript → textInput ───
  useEffect(() => {
    const fullTranscript = deepgram.transcript;
    if (fullTranscript.length > consumedTranscriptRef.current) {
      const newPart = fullTranscript.slice(consumedTranscriptRef.current).trim();
      if (newPart) {
        setTextInput(prev => prev + (prev ? ' ' : '') + newPart);
      }
      consumedTranscriptRef.current = fullTranscript.length;
    }
  }, [deepgram.transcript]);

  // ─── Stop → Edit transition ───
  useEffect(() => {
    if (pendingEditTransition && deepgram.connectionStatus === 'idle') {
      setPendingEditTransition(false);
      setPhase('editing');
    }
  }, [pendingEditTransition, deepgram.connectionStatus]);

  const handleStopAndEdit = useCallback(() => {
    if (deepgram.connectionStatus === 'recording' || deepgram.connectionStatus === 'paused') {
      // 今回の録音時間を蓄積（追加録音に対応）
      setRecordedDuration(prev => prev + deepgram.duration);
      deepgram.stopRecording();
      setPendingEditTransition(true);
    } else {
      setPhase('editing');
    }
  }, [deepgram]);

  const goToRecording = () => {
    deepgram.clear();
    consumedTranscriptRef.current = 0;
    setPhase('recording');
  };

  // ─── Save: upload content → AI processing happens server-side ───
  const handleSave = async () => {
    if (!textInput.trim()) return;

    setPhase('saving');
    setIsSubmitting(true);
    setSaveProgress(10);
    setSaveStatus('アップロード中...');

    try {
      const formData = new FormData();
      formData.append('content', textInput.slice(0, 3500));
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      formData.append('date', dateStr);
      const intensity = localStorage.getItem('aiIntensity') || 'light';
      formData.append('intensity', intensity);
      formData.append('duration_seconds', String(recordedDuration));

      // 過去日付の場合: created_at を指定
      if (!isToday(selectedDate)) {
        formData.append('created_at', `${dateStr}T${selectedTime}:00`);
      }

      if (intensity === 'raw' || searchParams.get('mode') === 'text') {
        formData.append('skipRewrite', 'true');
      }

      const tonePrompt = localStorage.getItem('tonePrompt') || '';
      if (tonePrompt) formData.append('tonePrompt', tonePrompt);
      if (localStorage.getItem('aiAutoTag') === 'false') {
        formData.append('skipTags', 'true');
      }

      setSaveProgress(30);
      setSaveStatus('AI分析中（タイトル・タグ・ムード生成）...');

      const { entry } = await api.createEntry(formData);

      setSaveProgress(100);
      setSaveStatus('保存完了');
      clearDraft();
      toast({ title: '保存できました' });
      navigate(`/entry/${entry.id}`);
    } catch (err: any) {
      if (err?.code === 'LIMIT_REACHED') {
        setShowPremiumModal(true);
      }
      toast({
        title: '保存に失敗しました',
        description: err instanceof Error ? err.message : undefined,
        variant: 'destructive',
      });
      setPhase('editing');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Discard confirmation dialog (editing phase) ───
  const discardDialog = (
    <Dialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>日記を破棄しますか？</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">保存されていない内容は失われます</p>
        <div className="flex gap-3 mt-4">
          <Button variant="outline" className="flex-1" onClick={() => setShowDiscardDialog(false)}>
            キャンセル
          </Button>
          <Button variant="destructive" className="flex-1" onClick={handleDiscard}>
            破棄する
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  // ─── Cancel confirmation dialog (recording phase X button) ───
  const cancelDialog = (
    <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>録音を削除しますか？</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">録音内容を削除してホーム画面に戻ります</p>
        <div className="flex gap-3 mt-4">
          <Button variant="outline" className="flex-1" onClick={() => setShowCancelDialog(false)}>
            戻る
          </Button>
          <Button variant="destructive" className="flex-1" onClick={handleDiscard}>
            削除する
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  // ─── Phase: Recording ───
  if (phase === 'recording') {
    return (
      <>
        {cancelDialog}
        <ImmersiveRecorder
          connectionStatus={deepgram.connectionStatus}
          segments={deepgram.segments}
          interimText={deepgram.interimText}
          streamingText={deepgram.streamingText}
          duration={deepgram.duration}
          isNearLimit={deepgram.isNearLimit}
          error={deepgram.error}
          analyserRef={deepgram.analyserRef}
          maxDuration={deepgram.maxDuration}
          onStart={deepgram.startRecording}
          onStop={() => handleStopAndEdit()}
          onPause={deepgram.pauseRecording}
          onResume={deepgram.resumeRecording}
          onCancel={handleCancel}
          hasTranscript={!!textInput.trim()}
        />
      </>
    );
  }

  // ─── Phase: Saving (progress screen) ───
  if (phase === 'saving') {
    return (
      <div className="fixed inset-0 z-50 bg-background flex flex-col items-center justify-center gap-6">
        {discardDialog}
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          className="rounded-full p-4 bg-primary/10"
        >
          <Loader2 className="h-10 w-10 text-primary animate-spin" />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="text-center space-y-3 w-full max-w-xs"
        >
          <h2 className="text-lg font-semibold">{saveStatus}</h2>
          <div className="w-full bg-muted rounded-full h-2.5 overflow-hidden">
            <motion.div
              className="bg-primary h-full rounded-full"
              initial={{ width: '0%' }}
              animate={{ width: `${saveProgress}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            />
          </div>
          <p className="text-xs text-muted-foreground">{saveProgress}%</p>
        </motion.div>
      </div>
    );
  }

  // ─── Phase: Editing (Markdown editor) ───
  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col">
      {discardDialog}

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <button onClick={handleBackPress} className="p-2 text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-5 w-5" />
        </button>
      </div>

      {/* Date + Time selector */}
      <div className="flex items-center gap-2 px-4 py-2 border-b">
        <button onClick={() => setShowCalendar(true)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <CalendarIcon className="h-4 w-4" />
          {format(selectedDate, 'yyyy年M月d日 (E)', { locale: ja })}
        </button>
        {!isToday(selectedDate) && (
          <div className="flex items-center gap-1.5 ml-auto">
            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
            <input
              type="time"
              value={selectedTime}
              onChange={e => setSelectedTime(e.target.value)}
              className="px-2 py-0.5 rounded-md border bg-background text-sm w-[100px]"
            />
          </div>
        )}
      </div>

      <Dialog open={showCalendar} onOpenChange={setShowCalendar}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>日付を選択</DialogTitle></DialogHeader>
          <Calendar selected={selectedDate} onSelect={(date) => { if (date) setSelectedDate(date); setShowCalendar(false); }} />
        </DialogContent>
      </Dialog>

      {/* Editor */}
      <div className="flex-1 overflow-y-auto relative">
        <Textarea
          ref={textareaRef}
          value={textInput}
          onChange={e => setTextInput(e.target.value)}
          onSelect={handleTextSelect}
          onMouseUp={handleTextSelect}
          placeholder="日記をここに入力..."
          className="min-h-full border-0 focus-visible:ring-0 p-4 text-base leading-relaxed resize-none shadow-none rounded-none"
          autoFocus
        />
        {/* 辞書登録ボタン（テキスト選択中のみ表示） */}
        {selectedText && (
          <button
            onClick={openDictDialog}
            className="absolute bottom-3 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-medium shadow-lg animate-in fade-in zoom-in-95 duration-150"
          >
            <BookOpen className="h-3.5 w-3.5" />
            辞書に追加
          </button>
        )}
      </div>

      {/* 辞書ルール追加ダイアログ */}
      <Dialog open={showDictDialog} onOpenChange={setShowDictDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              変換ルール追加
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium mb-1 block">変換前（誤変換）</label>
              <Input
                value={dictFind}
                onChange={e => setDictFind(e.target.value)}
                placeholder="独り言"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">変換後（正しい表記）</label>
              <Input
                value={dictReplace}
                onChange={e => setDictReplace(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleDictAdd()}
                placeholder="Hitorigoto"
                autoFocus
              />
            </div>
            <p className="text-xs text-muted-foreground">
              次回の文字起こしからリアルタイムで自動変換されます
            </p>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowDictDialog(false)}>
                キャンセル
              </Button>
              <Button
                className="flex-1"
                onClick={handleDictAdd}
                disabled={dictAdding || !dictFind.trim() || !dictReplace.trim() || dictFind.trim() === dictReplace.trim()}
              >
                {dictAdding ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                追加
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bottom toolbar */}
      <div className="p-4 border-t backdrop-blur-sm space-y-3">
        <Button variant="outline" onClick={goToRecording} className="gap-2 w-full rounded-xl">
          <div className="rounded-full p-0.5 bg-orange-500/10">
            <Mic className="h-3.5 w-3.5 text-orange-500" />
          </div>
          追加録音
        </Button>

        {/* Save button */}
        {textInput.trim() && (
          <Button onClick={handleSave} disabled={isSubmitting} size="lg" className="w-full gap-2 text-base">
            {isSubmitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
            {searchParams.get('mode') === 'text' ? '保存' : '仕上げる'}
          </Button>
        )}

        <p className="text-xs text-muted-foreground text-center">
          {textInput.length} / 3,500 文字<span className="hidden sm:inline"> • Ctrl+Enter で保存</span>
        </p>
      </div>

      <PremiumModal open={showPremiumModal} onOpenChange={setShowPremiumModal} />
    </div>
  );
}
